import { FC, useEffect } from 'react';

import { useSession } from '@eneco/auth';
import { useApplication } from '@eneco/common/application';
import { useChat, Namespace, ChatArgs, Visibility } from '@eneco/components/chat/useChat';

type ChatProps = {
  namespace: Namespace;
  fields?: {
    chatNamespace?: {
      id?: string;
      url?: string;
      name?: string;
      displayName?: string;
      fields?: {
        namespace?: {
          value?: Namespace;
        };
      };
    };
    chatProviderTopic?: {
      id?: string;
      url?: string;
      name?: string;
      displayName?: string;
      fields?: {
        namespace?: {
          value?: string;
        };
      };
    };
  };
};

const Chat: FC<ChatProps> = ({ namespace, fields }) => {
  const [mount] = useChat();
  const { searchParams } = useApplication();
  const topicToChat = searchParams.get('topicToChat');
  const textToChat = searchParams.get('textToChat');
  const stateToChat = searchParams.get('stateToChat') as Visibility;

  const askTextParam = searchParams.get('askText');
  const decodedAskText = askTextParam ? decodeURIComponent(askTextParam) : null;
  const topicFromUrl = searchParams.get('topic');

  const { data: session, status } = useSession() ?? {};

  const namespaceJSS = fields?.chatNamespace?.fields?.namespace?.value;
  const topicJSS = fields?.chatProviderTopic?.fields?.namespace?.value;

  useEffect(() => {
    return () => {
      mount({ visibility: 'hidden' });
    };
  }, [mount]);

  useEffect(() => {
    if (status === 'loading') {
      return;
    }

    const token = session?.idToken;
    if (decodedAskText) {
      mount({
        namespace: namespace ?? namespaceJSS,
        askText: decodedAskText,
        topic: topicFromUrl ?? 'urlparam',
        sendTopicAndAskText: true,
        visibility: 'open',
        api: {
          getConversationAuthToken: () => token,
        },
      });
      return;
    }

    const chatArgs: ChatArgs = {
      askText: textToChat ?? undefined,
      namespace: namespace ?? namespaceJSS,
      topic: topicToChat ?? topicJSS,
      context: textToChat
        ? {
            variables: {
              event: textToChat ? 'start_with_question' : undefined,
            },
          }
        : undefined,
      visibility: stateToChat ?? (textToChat || topicToChat ? 'open' : undefined),
    };

    mount({
      ...chatArgs,
      api: {
        getConversationAuthToken: () => token,
      },
    });
  }, [
    decodedAskText,
    mount,
    namespace,
    namespaceJSS,
    session,
    stateToChat,
    status,
    textToChat,
    topicJSS,
    topicFromUrl,
    topicToChat,
  ]);

  return null;
};

export default Chat;
